import {isNil, notNil, notEmpty, areJSONEqual, isEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {log, warn} from '@megaphonenpm/mputils/src/logfunctions.js';
import {EventDispatcher} from '@megaphonenpm/mputils/src/EventDispatcher.js';
import {
  STATE_END, STATE_INSTRUCTIONS, STATE_INTERMISSION,
  STATE_INTRODUCTION, STATE_MAIN_COUNTDOWN, STATE_MAIN_COUNTDOWN_ENDPAUSE, STATE_MAIN_SCOREBOARD,
  STATE_SCENE,
  STATE_SCENE_RESULT, STATE_SCENE_SCOREBOARD, STATE_WINNER
} from "./constants.js";

import {
  CHANGE,
  CLOSE, COMPLETE, FAIL,
  READY
} from '@megaphonenpm/mputils/src/constants.js';

import {
  ADD_SCENE_TOTALS,
  CONNECT_SCREEN, DATA_CONTENT,
  DATA_CONTENT_MANIFEST,
  DATA_DEPLOYMENT_SETTINGS,
  DATA_RECENT_RESPONSES,
  DATA_RECENT_CALLERS,
  DATA_ROUND_RANKINGS,
  DATA_RUN,
  DATA_SCENE,
  DATA_GLOBAL,
  GOTO_MAIN_SCOREBOARD,
  NEXT,
  PAUSE_RESUME,
  RELOAD_CONTENT,
  RESTART_ROOM,
  SCREEN_READY,
  SET_NEXT_SCENE_BY_ID,
  SET_STATE_GAME_WINNER,
  SET_STATE_GAMEOVER,
  SET_STATE_INSTRUCTIONS,
  SET_STATE_INTERMISSION,
  SET_STATE_INTRODUCTION,
  SET_STATE_MAIN_COUNTDOWN,
  SET_STATE_MAIN_COUNTDOWN_ENDPAUSE,
  SET_STATE_MAIN_SCOREBOARD,
  SET_STATE_ROUND,
  SET_STATE_ROUND_ENDPAUSE,
  SET_STATE_ROUND_SCOREBOARD, START_GAME,
  UPDATE_STATE_GAME_WINNER,
  UPDATE_STATE_INSTRUCTIONS, UPDATE_STATE_MAIN_COUNTDOWN,
  UPDATE_STATE_MAIN_SCOREBOARD,
  UPDATE_STATE_ROUND,
  UPDATE_STATE_ROUND_ENDPAUSE,
  SET_VOTE_VISIBILITY, DATA_STATE, SET_PLAYLIST_AUTOUPDATE
} from './MRTSMessages.js';
import {MRTSConnectionEvent} from "./MRTSConnectionEvent.js";
import {AppStateUpdate} from '@megaphonenpm/mputils/src/AppStateUpdate.js';
import moment from 'moment/src/moment.js';
import {get} from 'lodash-es';
import {WatchableObject} from '@megaphonenpm/mputils/src/WatchableObject.js';
import {ThemeService} from './ThemeService.js';
import {GetEditorThemeSetsTask} from './GetEditorThemeSetsTask.js';

export class MRTSConnection extends EventDispatcher {
  /**
   * @param appState A MRTSClientAppState object to which MRTS's current state will be written.
   * @param dataStore An object to which MRTS data will be written. (Playlist, themes, themesets, etc.)
   * @param observeChannel If true, MRTSConnection will ask to observe the StreamClick channel room. Required
   *          by Director, which observes the channel room and proxies data between MRTS and child applications
   *          such as Switcher and Roll Clip.
   */
  constructor (appState, dataStore, observeChannel = false) {
    super();
    this.appState = appState;
    this.dataStore = dataStore;
    this.themeService = new ThemeService(this.appState, this.dataStore);
    this.observeChannel = observeChannel;
    this.serverModule = 'MegaphoneServices';
    this.attributes = null;
    this.screenReadyTime = 0;
    this.isScreenReady = false;
    this.numScreenReadies = 0;
    this.roomID = null;
    this.bufferedUpdate = null;
    this.connectionStartTime = 0;
    this.timeToOrbiterReady = 0;
    this.timeToScreenReady = 0;
    this.screenTimeoutID = -1;
    this.contentReadyReceivedCount = 0;  // Allows clients to distinguish first content load from
                                         // content loads in response to RELOAD_CONTENT (s11).
    this.configureOrbiter();
  }

  configureOrbiter () {
    // Create the Orbiter object, which conducts all network communications with MRTS
    this.orbiter = new net.user1.orbiter.Orbiter();

    // Register for Orbiter events
    // TODO: Add optional Orbiter logging to the debugger and browser console. Example:
    //  this.orbiter.getLog().setLevel(net.user1.logger.Logger.DEBUG);
    //  this.orbiter.enableConsole();
    this.orbiter.addEventListener(net.user1.orbiter.OrbiterEvent.READY, this.readyListener, this);
    this.orbiter.addEventListener(net.user1.orbiter.OrbiterEvent.CLOSE, this.closeListener, this);
    this.orbiter.getConnectionManager().addEventListener(net.user1.orbiter.ConnectionManagerEvent.BEGIN_CONNECT, this.beginConnectListener, this);
    this.orbiter.getConnectionManager().addEventListener(net.user1.orbiter.ConnectionManagerEvent.CONNECT_FAILURE, this.connectFailureListener, this);
    this.orbiter.getConnectionManager().addEventListener(net.user1.orbiter.ConnectionManagerEvent.DISCONNECT, this.disconnectListener, this);

    // Configure the autoreconnect system
    this.orbiter.getConnectionMonitor().setAutoReconnectFrequency(MRTSConnection.AUTO_RECONNECT_FREQUENCY);
    this.orbiter.getConnectionMonitor().setConnectionTimeout(MRTSConnection.CONNECTION_TIMEOUT);
    this.orbiter.getConnectionMonitor().setHeartbeatFrequency(MRTSConnection.HEARTBEAT_FREQUENCY);

    // Register Orbiter and Union message listeners
    this.orbiter.getConnectionManager().addEventListener(net.user1.orbiter.ConnectionManagerEvent.SELECT_CONNECTION,
      () => {
        this.registerMessageListeners();
      });
  }

  configureOrbiterConnections () {
    // TODO: Allow connection types to be specified externally (e.g., always connect over HTTP)
    // TODO: WHEN MRTS CAN PROCESS HTTP REQUESTS WITHOUT ERRORS AGAIN, REINSTATE THE
    //  HTTP CONNECTION TYPE IN THE FOLLOWING LINE OF CODE. SEE TICKET #93 HERE:
    //  https://playmegaphone.unfuddle.com/a#/projects/36904/tickets/by_number/93
    let connectionTypes = [net.user1.orbiter.ConnectionType.SECURE_WEBSOCKET];
    this.dataStore.mrtsEndpoint.ports.forEach((port) => {
      connectionTypes.forEach((connectionType) => {
        this.orbiter.buildConnection(this.dataStore.mrtsEndpoint.host, port, connectionType, net.user1.orbiter.HTTPConnection.DEFAULT_SEND_DELAY);
      });
    });
  }

  resetAttributes () {
    this.attributes = {};
  }

  registerMessageListeners () {
    // Register Union message listeners
    this.orbiter.getMessageManager().addMessageListener(net.user1.orbiter.UPC.STOPPED_OBSERVING_ROOM, this.stopObservingRoomListener, this);
    this.orbiter.getMessageManager().addMessageListener(net.user1.orbiter.UPC.OBSERVED_ROOM, this.observedRoomListener, this);
    this.orbiter.getMessageManager().addMessageListener(net.user1.orbiter.UPC.CLIENT_ATTR_UPDATE, this.clientAttributeUpdateListener, this);

    // Register MRTS message listeners
    this.orbiter.getMessageManager().addMessageListener(SCREEN_READY, this.screenReadyListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_CONTENT_MANIFEST, this.dataContentManifestListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_CONTENT, this.dataContent, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_RUN, this.dataRunListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_DEPLOYMENT_SETTINGS, this.dataDeploymentSettingsListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_SCENE, this.dataSceneListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_ROUND_RANKINGS, this.dataRoundRankingsListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_RECENT_RESPONSES, this.dataRecentResponsesListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_RECENT_CALLERS, this.dataRecentCallersListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_GLOBAL, this.dataGlobalListener, this);
    this.orbiter.getMessageManager().addMessageListener(DATA_STATE, this.dataStateListener, this);
    this.orbiter.getMessageManager().addMessageListener(UPDATE_STATE_INSTRUCTIONS, this.updateStateInstructionsListener, this);
    this.orbiter.getMessageManager().addMessageListener(UPDATE_STATE_MAIN_COUNTDOWN, this.updateStateMainCountdownListener, this);
    this.orbiter.getMessageManager().addMessageListener(UPDATE_STATE_ROUND, this.updateStateRoundListener, this);
    this.orbiter.getMessageManager().addMessageListener(UPDATE_STATE_ROUND_ENDPAUSE, this.updateStateRoundEndPauseListener, this);
    this.orbiter.getMessageManager().addMessageListener(UPDATE_STATE_MAIN_SCOREBOARD, this.updateStateMainScoreboardListener, this);
    this.orbiter.getMessageManager().addMessageListener(UPDATE_STATE_GAME_WINNER, this.updateStateGameWinnerListener, this);
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_INTRODUCTION,            () => { this.setAppState(STATE_INTRODUCTION)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_INSTRUCTIONS,            () => { this.setAppState(STATE_INSTRUCTIONS)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_MAIN_COUNTDOWN,          () => { this.setAppState(STATE_MAIN_COUNTDOWN)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_MAIN_COUNTDOWN_ENDPAUSE, () => { this.setAppState(STATE_MAIN_COUNTDOWN_ENDPAUSE)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_ROUND,                   () => { this.setAppState(STATE_SCENE )});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_ROUND_ENDPAUSE,          () => { this.setAppState(STATE_SCENE_RESULT )});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_ROUND_SCOREBOARD,        () => { this.setAppState(STATE_SCENE_SCOREBOARD)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_GAMEOVER,                () => { this.setAppState(STATE_END)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_GAME_WINNER,             () => { this.setAppState(STATE_WINNER)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_MAIN_SCOREBOARD,         () => { this.setAppState(STATE_MAIN_SCOREBOARD)});
    this.orbiter.getMessageManager().addMessageListener(SET_STATE_INTERMISSION,            () => { this.setAppState(STATE_INTERMISSION)});
  }

  // ===============================================================================================
  // ORBITER NETWORKING
  // ===============================================================================================
  connect () {
    this.orbiter.connect();
  }

  // ===============================================================================================
  // ORBITER EVENT LISTENERS
  // ===============================================================================================
  readyListener (e) {
    // Note: This is Orbiter's low-level ready, not MRTS's SCREEN_READY
    this.timeToOrbiterReady = moment().diff(this.connectionStartTime);
    log(`Orbiter connection ready. Client ID: [${this.orbiter.getClientID()}]. Elapsed since connect(): ${this.timeToOrbiterReady}ms`);
    this.dispatchEvent(READY);
    this.sendConnectScreen();
  }

  /**
   * Handles both Orbiter connection-attempt failure AND disconnect (i.e., the current connection
   * was severed, either at the client or the server's instigation).
   *
   * @param e
   */
  closeListener (e) {
    warn('Orbiter connection closed.');
    this.isScreenReady = false;
    clearTimeout(this.screenTimeoutID);
    this.dispatchEvent(CLOSE);
  }

  beginConnectListener (e) {
    this.connectionStartTime = moment();
    this.resetAttributes();
    log('Beginning Orbiter connection attempt...')

  }

  connectFailureListener (e) {
    warn(`FAILED TO CONNECT TO MRTS WITHIN ${this.orbiter.getConnectionManager().readyTimeout}ms.`);
  }

  disconnectListener (e) {
    log('Orbiter disconnected.')
  }

  /**
   * This method is triggered when MRTS forces the client to stop observing the server-side
   * application room, normally because the room has been removed (e.g., during a "Reset Application").
   *
   * In response, the MRTS client can choose to attempt a new connection attempt to MRTS. If the
   * MRTS-connection attempt succeeds, then all content is also reloaded from scratch (playlist,
   * media, settings, themes, etc).
   *
   * @param e
   */
  stopObservingRoomListener (e) {
    window.location.reload();
  }

  /**
   * This method is triggered when MRTS forces the MRTS client to start observing the server-side
   * application room, normally because the client has has sent a prior CONNECT_SCREEN request.
   *
   * @param roomID The ID of the application room. In practice, this is typically the same as, but
   *   not guaranteed to be, the deployment key.
   */
  observedRoomListener (roomID) {
    // Store the roomID so that it can be used later to detect UPC messages by broadcastType (either
    // "to_clients" or "to_rooms"). If the roomID ends with "channel", it is the StreamClick channel
    // room, not the application room; ensure that this.roomID refers to the application room.
    if (!roomID.endsWith('channel')) {
      this.roomID = roomID;
    }
  }

  /**
   * Handles changes in Orbiter's low-level client attributes (normally set by MRTS).
   * Example attributes include:
   * _IP (IP Address)
   * _CT (Time on the server when this client connected)
   */
  clientAttributeUpdateListener (roomID,
                                 clientID,
                                 userID,
                                 attrName,
                                 attrVal,
                                 attrOptions) {
    this.attributes[attrName] = attrVal;

    // Store "instance ID" locally for later transmission to MRTS if a reconnection occurs.
    // The "instance ID" is best thought of as a "screen session id". Providing MRTS with the
    // "instance id" forces MRTS to treat this running client as the same client "instance"
    // (i.e., session) no matter how many times it connects. If this MRTS client is rebooted, it
    // will request a new instance ID from MRTS (i.e., it will start a new "session").
    if (attrName === 'INSTANCE_ID') {
      this.dataStore.instanceID = attrVal;
    }

    this.dispatchEvent(CHANGE, {name: attrName, value: attrVal});
  };

  // ===============================================================================================
  // MESSAGES TO MRTS
  // ===============================================================================================
  sendConnectScreen () {
    // TODO: When MRTS supports deployment_id, change deployment_key to deployment_id
    // TODO: Remove installation_key when MRTS removes it. Will no longer be used when NEO is release.
    let args = {
      'instance[id]': this.dataStore.instanceID,
      'instance[deployment_key]': this.dataStore.deploymentKey,
      'instance[password]': this.dataStore.deploymentPassword,
      'instance[installation_key]': 'main',
      'observe_channel': this.observeChannel ? 'true' : 'false'
    };
    this.sendServerModuleMessage(CONNECT_SCREEN, args);
    clearTimeout(this.screenTimeoutID);
    this.screenTimeoutID = setTimeout(() => {
      if (!this.isScreenReady) {
        // If the screen did not achieve a ready state within the allowed SCREEN_TIMEOUT, then
        // attempt to reconnect to MRTS, which will automatically abort the in-progress connection.
        warn(`CONNECTED TO MRTS, BUT FAILED TO ACHIEVE SCREEN READY FOR ${MRTSConnection.SCREEN_TIMEOUT}ms. Forcing disconnect...`);
        this.orbiter.connect();
      }
    }, MRTSConnection.SCREEN_TIMEOUT);
  };

  sendStartGame () {
    this.sendRoomModuleMessage(START_GAME);
  };

  sendNext () {
    this.sendRoomModuleMessage(NEXT);
  };

  sendPauseResume () {
    this.sendRoomModuleMessage(PAUSE_RESUME);
  };

  sendRestartRoom () {
    this.sendRoomModuleMessage(RESTART_ROOM);
  };

  sendReloadContent () {
    warn('[MRTS_CLIENT] Sending RELOAD_CONTENT (S11) to MRTS.');
    console.trace();
    this.sendRoomModuleMessage(RELOAD_CONTENT);
  };

  sendGotoMainScoreboard () {
    this.sendRoomModuleMessage(GOTO_MAIN_SCOREBOARD);
  };

  sendSetNextScene (sceneID) {
    this.sendRoomModuleMessage(SET_NEXT_SCENE_BY_ID, {sceneID: sceneID});
  };

  /**
   * Adds the provided votes to the existing vote totals in the current scene.
   *
   * @param counts An array of integers indicating the number of votes to add to each
   *               answer in the current poll. Example:
   */
  sendAddSceneTotals (counts) {
    if (notNil(counts)) {
      let countsString = Array.isArray(counts) ? counts.join(',') : counts.toString();
      this.sendRoomModuleMessage(ADD_SCENE_TOTALS, {counts: countsString});
    }
  };

  sendSetVoteVisibility (screenVisible = '', controllerVisible = '') {
    let voteVisibility = {};
    voteVisibility.screen = screenVisible;
    voteVisibility.controller = controllerVisible;

    this.sendRoomModuleMessage(SET_VOTE_VISIBILITY, {visibility: JSON.stringify(voteVisibility)});
  };

  sendSetPlaylistAutoUpdate (autoupdate) {
    this.sendRoomModuleMessage(SET_PLAYLIST_AUTOUPDATE, {enabled: autoupdate});
  };

  /**
   * Sends the specified "module message" to MRTS.
   *
   * For an exhaustive list of available messages, see:
   *   https://docs.google.com/spreadsheets/u/1/d/1Jzl0FYy6XZnCMuL4AL_FD9HPaFH7L5qu4kMMWlRtT-g/edit?usp=drive_web&ouid=104619248380361287499
   *
   * MRTSConnection's send() method is a helper function that provides a simpler wrapper API for
   * MRTS communications on top of Orbiter's generic lower-level communications API.
   *
   * @param messageName A MRTS module message.
   *
   * @param args The arguments to the module message, as an generic object of key/value pairs.
   *   For example, {'next_scene_id': '1e2ff34c257'}
   */
  sendServerModuleMessage (messageName, args) {
    this.orbiter.getMessageManager().sendUPC(net.user1.orbiter.UPC.SEND_SERVERMODULE_MESSAGE,
      this.serverModule,
      messageName,
      ...MRTSConnection.toUPCArgs(args));
  }

  sendRoomModuleMessage (messageName, args) {
    this.orbiter.getMessageManager().sendUPC(net.user1.orbiter.UPC.SEND_ROOMMODULE_MESSAGE,
      this.dataStore.deploymentKey,
      messageName,
      ...MRTSConnection.toUPCArgs(args));

    this.dispatchEvent(MRTSConnectionEvent.ROOM_MODULE_MESSAGE_SENT, {messageName: messageName, args: args});
  }

  // ===============================================================================================
  // MESSAGES FROM MRTS
  // ===============================================================================================
  screenReadyListener () {
    this.screenReadyTime = moment();
    this.timeToScreenReady = this.screenReadyTime.diff(this.connectionStartTime + this.timeToOrbiterReady);
    log(`MRTS screen ready. Elapsed since sendConnectScreen(): ${this.timeToScreenReady}ms`);
    this.isScreenReady = true;
    this.numScreenReadies++;
    this.sendStartGame();
    this.dispatchEvent(SCREEN_READY);
  }

  /**
   * This listener is a no-op that receives s4 (DATA_CONTENT_MANIFEST) messages from MRTS and does
   * nothing with them. Without this message listener, Orbiter's log would report a warning that
   * the message was received, but no listener handled it.
   *
   * The s4 message is a legacy message that contains the "questions xml," which
   * is the deployment's active playlist converted from JSON to XML format by Editor Service.
   * The following code demonstrates how to handle the questions xml ("manifest"):
   *   let domParser = new DOMParser();
   *   let playlistDOM = domParser.parseFromString(manifest, "application/xml");
   *   log(playlistDOM.querySelectorAll('question content')[0].textContent);  // Log question text
   *
   * @param from The id of the Union client that sent the message. In the case of MRTS, the from
   *   paramter is always empty because messages are sent by a server-side room module.
   * @param manifest The current playlist (also known as the "content manifest") in XML format.
   */
  dataContentManifestListener (from, manifest) {
    // No-op
  }

  /**
   * Handles DATA_RUN (s13) messages from MRTS.
   *
   * Delegates to processDataRun() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  dataRunListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processDataRun.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_RUN messages from MRTS.
   */
  processDataRun (roomStart, runID, runStart) {
    this.dispatchEvent(DATA_RUN, {roomStart: Number(roomStart), runID: runID, runStart:Number(runStart)});
  }

  /**
   * Handles DATA_DEPLOYMENT_SETTINGS (s14) messages from MRTS.
   */
  dataDeploymentSettingsListener (from, deploymentSettings) {
    if (this.dataStore instanceof WatchableObject) {
      // For dataStore objects that inherit from WatchableObject, use setCompositeProperty() so that
      // assignments of property values that have not changed do not trigger a CHANGE event.
      this.dataStore.setCompositeProperty('deploymentSettings', JSON.parse(deploymentSettings));
    } else {
      this.dataStore.deploymentSettings = JSON.parse(deploymentSettings);
    }
    this.dispatchEvent(MRTSConnectionEvent.DEPLOYMENT_SETTINGS_READY);
  }

  /**
   * Handles DATA_CONTENT (s16) messages from MRTS.
   */
  dataContent (from, ...args) {
    this.contentReadyReceivedCount++;
    MRTSConnection.adaptToBroadcastType(this.processDataContent.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_CONTENT messages from MRTS.
   */
  processDataContent (playlist, themesets, themes, products) {
    let parsedPlaylist = JSON.parse(playlist);
    let parsedThemeSets = JSON.parse(themesets);
    let parsedThemes = JSON.parse(themes);

    warn(`[MRTS_CONNECTION] [s16] Received s16. Old playlist: [updated.at: `
      + `${get(this.dataStore, 'playlist.updated.at', 'none')}, version: ${get(this.dataStore, 'playlist.playlist_version', 'none')}]`
      + ` New playlist: [updated.at: ${get(parsedPlaylist, 'updated.at', 'none')}, version: ${get(parsedPlaylist, 'playlist_version', 'none')}]`);
    warn(`[MRTS_CONNECTION] [s16] Comparing new and old playlists: areJSONEqual()? ${areJSONEqual(this.dataStore.playlist, parsedPlaylist)}`);

    if (this.dataStore instanceof WatchableObject) {
      // For dataStore objects that inherit from WatchableObject, use setCompositeProperty() so that
      // assignments of property values that have not changed do not trigger a CHANGE event.
      this.dataStore.setCompositeProperty('playlist', parsedPlaylist);
      this.dataStore.setCompositeProperty('themeSets', parsedThemeSets);
      this.dataStore.setCompositeProperty('themes', parsedThemes);
    } else {
      this.dataStore.playlist = parsedPlaylist;
      this.dataStore.themeSets = parsedThemeSets;
      this.dataStore.themes = parsedThemes;
    }
    warn(`[MRTS_CONNECTION] [s16] Processing complete. dataStore playlist after assignment: [updated.at: `
      + `${get(this.dataStore, 'playlist.updated.at', 'none')}, version: ${get(this.dataStore, 'playlist.playlist_version', 'none')}]`);

    let idsOfMissingThemeSets = this.themeService.getIDsOfMissingThemeSets();

    if (idsOfMissingThemeSets.length === 0) {
      // All required ThemeSets are available, so dispatch CONTENT_READY
      this.dispatchEvent(MRTSConnectionEvent.CONTENT_READY);
    } else {
      // Some required ThemeSets are NOT available. If the current client is logged into Studio
      // AND the missing ThemeSets have not already been loaded, load them now.
      if (isEmpty(this.dataStore.studioSessionToken)) {
        // The current client is not logged into Studio (which is required to load ThemeSets), so
        // don't attempt to load missing ThemeSets. Simply dispatch CONTENT_READY.
        this.dispatchEvent(MRTSConnectionEvent.CONTENT_READY);
      } else {
        // The current client is logged into Studio, so missing ThemeSets can be loaded.
        // Make a list of "unresolved" missing ThemeSets. A "unresolved" ThemeSet is one that has
        // not previously been identified as missing from the project.
        let idsOfUnresolvedMissingThemeSets = [];
        idsOfMissingThemeSets.forEach(themeSetID => {
          if (this.dataStore.themeSetsMissingFromProject.has(themeSetID)
            || this.dataStore.idsOfThemeSetsMissingFromSystem.has(themeSetID)) {
            // This themesetID has already been resolved (i.e., was previously identified as being
            // missing from the project or the system, and subsequently loaded or failed to load).
            // Skip to the next themesetID.
            return;
          }
          // This themesetID is unresolved. Add it to the list of missing themesetIDs to resolve.
          idsOfUnresolvedMissingThemeSets.push(themeSetID);
        })

        // Check if any of the missing ThemeSets are unresolved. If so, load them.
        if (idsOfUnresolvedMissingThemeSets.length > 0) {
          // Some of the missing ThemeSets are unresolved. Load them now. If a ThemeSet can't be found
          // by Editor Service, it will be omitted from the results returned by GetEditorThemeSetsTask.
          let getEditorThemeSetsTask = new GetEditorThemeSetsTask(idsOfUnresolvedMissingThemeSets, this.dataStore.environment, this.dataStore.studioSessionToken);
          getEditorThemeSetsTask.on(COMPLETE, () => {
            // Finished loading unresolved missing ThemeSets. Now, for every missing ThemeSet that was
            // previously unresolved, if it loaded successfully, add it to the DataStore's known missing
            // ThemeSets; otherwise, add it to the list of ThemeSet IDs that could not be found anywhere
            // in the system, even by Editor Service.
            let missingProjectThemeSetsChanged = false;
            let missingSystemThemeSetsChanged = false;
            idsOfUnresolvedMissingThemeSets.forEach(themesetID => {
              let loadedMissingThemeSet = getEditorThemeSetsTask.themesets.find(themeset => themeset.id === themesetID);

              if (notNil(loadedMissingThemeSet)) {
                // ThemeSet loaded
                this.dataStore.themeSetsMissingFromProject.set(themesetID, loadedMissingThemeSet);
                missingProjectThemeSetsChanged = true;
              } else {
                // ThemeSet failed to load
                this.dataStore.idsOfThemeSetsMissingFromSystem.add(themesetID);
                missingSystemThemeSetsChanged = true;
              }
            });
            if (missingProjectThemeSetsChanged) {
              // At least one ThemeSet that is missing from the project was added to the
              // themeSetsMissingFromProject map, so dispatch a CHANGE event to notify DataStore
              // observers.
              this.dataStore.dispatchEvent(CHANGE, {prop: 'themeSetsMissingFromProject', value: this.dataStore.themeSetsMissingFromProject, oldValue: null});
            }
            if (missingSystemThemeSetsChanged) {
              // At least one ThemeSet that is missing from the system was added to the
              // idsOfThemeSetsMissingFromSystem set, so dispatch a CHANGE event to notify DataStore
              // observers.
              this.dataStore.dispatchEvent(CHANGE, {prop: 'idsOfThemeSetsMissingFromSystem', value: this.dataStore.idsOfThemeSetsMissingFromSystem, oldValue: null});
            }

            // All missing ThemeSets have been resolved (loaded or found to be missing), so
            // dispatch CONTENT_READY
            this.dispatchEvent(MRTSConnectionEvent.CONTENT_READY);
          });
          getEditorThemeSetsTask.on(FAIL, () => {
            // The attempt to load missing ThemeSets failed completely. No further remedial action
            // can be taken, so dispatch CONTENT_READY.
            console.error(`[ThemeService] ATTEMPT TO LOAD MISSING THEMESETS FAILED. THEMESET IDs: ${idsOfUnresolvedMissingThemeSets}`);

            this.dispatchEvent(MRTSConnectionEvent.CONTENT_READY);
          });
          getEditorThemeSetsTask.start();
        } else {
          // All missing ThemeSets have already previously been resolved (loaded or found to be
          // missing), so dispatch CONTENT_READY
          this.dispatchEvent(MRTSConnectionEvent.CONTENT_READY);
        }
      }
    }
  }

  /**
   * Handles DATA_SCENE (s17) messages from MRTS, which contain the scene object for the current
   * state, and the default themeset id for the playlist that originally contained that scene object.
   * MRTS clients (such as MegaScreen) can rely on the scene object provided by s17 rather a local
   * playlist object to guarantee that the current client-side scene object always matches the
   * server's current scene object, even after an s11 ("Update Playlist") occurs.
   */
  dataSceneListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processDataScene.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_SCENE messages from MRTS.
   *
   * @param playlistThemesetID The default themeset id of the playlist that originally contained
   *          the specified scene. Used to determine the original playlist default themeset
   *          for the current scene even after s11 ("Update Playlist") occurs.
   *
   * @param scene The scene object for the current scene.
   */
  processDataScene (playlistThemesetID, scene) {
    scene = JSON.parse(scene);
    // Send the scene object to interested consumers before the state changes. This handles
    // advanced cases such as determining whether the new scene is in the active playlist, and if
    // not, loading the scene's media. For an example, see MegaScreen's OrphanSceneMediaLoader.
    this.dispatchEvent(MRTSConnectionEvent.SCENE_RECEIVED, {scene: scene});
    this.appState.setPrimitiveProperty('themeset_id_from_current_scenes_playlist', playlistThemesetID);
    this.appState.setCompositeProperty('scene', scene, true);
  }

  /**
   * Handles DATA_ROUND_RANKINGS messages from MRTS, which contain top-ranked users for the scene.
   * Delegates to processDataRoundRankings() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  dataRoundRankingsListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processDataRoundRankings.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_ROUND_RANKINGS messages from MRTS.
   */
  processDataRoundRankings (rankings) {
    this.appState.setCompositeProperty('sceneRankings', this.parseRankings(rankings));
  }

  /**
   * Handles DATA_RECENT_RESPONSES messages from MRTS, which contain recent answers to the current
   * question by a selected group of users. Delegates to processDataRecentResponses() due to the need
   * to strip the roomID argument when the UPC broadcastType is "to_rooms".
   */
  dataRecentResponsesListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processDataRecentResponses.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_RECENT_RESPONSES messages from MRTS.
   */
  processDataRecentResponses (responses) {
    if (notEmpty(responses)) {
      let appStateUpdate = new AppStateUpdate(this.appState, STATE_SCENE);
      // Use setDirectProperty() to ensure that every assignment triggers appState's watch listeners.
      // If this code used setCompositeProperty(), then if the new responses were the same as the last
      // batch of responses, the update would be detected as redundant by setCompositeProperty() and
      // discarded without triggering a CHANGE event.
      appStateUpdate.setDirectProperty('recentResponses', this.parseResponses(responses));

      this.ingestUpdate(appStateUpdate);

    }
  }

  /**
   * Handles DATA_GLOBAL messages from MRTS, which contain data updates that do not pertain to a
   * specific scene. Delegates to processDataGlobal() due to the need to strip the roomID argument
   * when the UPC broadcastType is "to_rooms".
   */
  dataGlobalListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processDataGlobal.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_GLOBAL messages from MRTS.
   */
  processDataGlobal (data) {
    let thisUpdate = (JSON.parse(data));
    // Defend against the case where voteVisibility is the empty string, which is not valid JSON
    let voteVisibility = get(thisUpdate, 'voteVisibility', '').length > 0 ? JSON.parse(thisUpdate.voteVisibility) : '';

    // Note that data received from MRTS via DATA_GLOBAL is not specific to any individual state,
    // so there's no need to potentially buffer the update via AppStateUpdate and
    // this.ingestUpdate(). Value assignments can and should be made directly on this.appState.
    this.appState.setPrimitiveProperty('numUsers', thisUpdate.currentUsers);
    this.appState.setPrimitiveProperty('runLifetimeNumUsers', thisUpdate.lifetimeUsers);
    this.appState.setCompositeProperty('voteVisibility', voteVisibility);
    this.appState.setPrimitiveProperty('explicitNextSceneID', thisUpdate.explicitNextSceneID);
  }

  /**
   * Handles DATA_STATE messages from MRTS, which contain data updates for the current run state.
   * Delegates to processDataState() due to the need to strip the roomID argument
   * when the UPC broadcastType is "to_rooms".
   */
  dataStateListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processDataState.bind(this), args, this.roomID);
  }

  /**
   * Processes DATA_STATE (s20) messages from MRTS.
   *
   * @param data A JSON object containing properties describing MRTS's current run state. The data
   *             object includes only those fields that have changed since the last s20. For example,
   *             if playlistUpdateAvailable has changed and no other fields have changed, then s20's
   *             data parameter will be:
   *             { "playlistUpdateAvailable": false }
   */
  processDataState (data) {
    if (data === 'true' || data === 'false') {
      // Legacy implementation, where the payload was a single parameter, scoredRun, whose value was
      // either the string "true" or the string "false".
      // This branch can be completely removed after all MRTS servers older than 540.3 have been
      // permanently decommissioned.
      this.appState.setPrimitiveProperty('scoredRun', data === 'true'); // Convert string from MRTS to Boolean
    } else {
      // DATA_STATE's sole argument changed from "scoredRun" to "data" in MRTS version 540.3.
      let thisUpdate = (JSON.parse(data));

      // Note that data received from MRTS via DATA_STATE should be processable by any state at any
      // time, so there's no need to potentially buffer the update via AppStateUpdate and
      // this.ingestUpdate(). Value assignments can and should be made directly on this.appState.
      if (thisUpdate.hasOwnProperty('scoredRun')) {
        this.appState.setPrimitiveProperty('scoredRun', thisUpdate.scoredRun);
      }
      if (thisUpdate.hasOwnProperty('fulfillmentCancelled')) {
        this.appState.setPrimitiveProperty('fulfillmentCancelled', thisUpdate.fulfillmentCancelled);
      }
      if (thisUpdate.hasOwnProperty('playlistAutoUpdate')) {
        this.appState.setPrimitiveProperty('playlistAutoUpdate', thisUpdate.playlistAutoUpdate);
      }
      if (thisUpdate.hasOwnProperty('playlistUpdateAvailable')) {
        this.appState.setPrimitiveProperty('playlistUpdateAvailable', thisUpdate.playlistUpdateAvailable);
      }
    }
  }

  /**
   * Handles DATA_RECENT_CALLERS messages from MRTS, which contain a list of recently connected
   * users. This message is currently unused.
   */
  dataRecentCallersListener (from, ...args) {
    // Intentionally do nothing.
  }

  /**
   * Handles UPDATE_STATE_INSTRUCTIONS messages from MRTS.
   *
   * Delegates to processUpdateStateInstructions() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  updateStateInstructionsListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processUpdateStateInstructions.bind(this), args, this.roomID);
  }

  /**
   * Processes UPDATE_STATE_INSTRUCTIONS messages from MRTS.
   */
  processUpdateStateInstructions (numCallers,  // Deprecated. Replaced by DATA_GLOBAL (s18)'s currentUsers.
                                  countdownCount) {
    let appStateUpdate = new AppStateUpdate(this.appState, STATE_INSTRUCTIONS);
    appStateUpdate.setPrimitiveProperty('timeRemaining', countdownCount);

    this.ingestUpdate(appStateUpdate);
  }

  /**
   * Handles UPDATE_STATE_MAIN_COUNTDOWN messages from MRTS.
   *
   * Delegates to processUpdateStateMainCountdown() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  updateStateMainCountdownListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processUpdateStateMainCountdown.bind(this), args, this.roomID);
  }

  /**
   * Processes UPDATE_STATE_MAIN_COUNTDOWN messages from MRTS.
   */
  processUpdateStateMainCountdown (countdownCount) {
    let appStateUpdate = new AppStateUpdate(this.appState, STATE_MAIN_COUNTDOWN);
    appStateUpdate.setPrimitiveProperty('timeRemaining', countdownCount);

    this.ingestUpdate(appStateUpdate);
  }

  /**
   * Handles UPDATE_STATE_ROUND messages from MRTS, which supply the latest information for the
   * current scene, such as votes, time left, and number of currently connected users.
   *
   * Delegates to processUpdateStateRound() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  updateStateRoundListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processUpdateStateRound.bind(this), args, this.roomID);
  }

  /**
   * Processes UPDATE_STATE_ROUND messages from MRTS.
   */
  processUpdateStateRound (questionNum,
                           totalNumQuestions,
                           phase,
                           numCallers,  // Deprecated. Replaced by DATA_GLOBAL (s18)'s currentUsers.
                           correctCallerCount,
                           incorrectCallerCount,
                           countdownCount,
                           pointsAvailable,
                           totalResponsesPerAnswer,
                           questionIndexInSourceSet) {
    let appStateUpdate = new AppStateUpdate(this.appState, STATE_SCENE);
    appStateUpdate.setCompositeProperty('votes', this.parseVotes(totalResponsesPerAnswer));
    appStateUpdate.setPrimitiveProperty('pointsRemaining', pointsAvailable);
    appStateUpdate.setPrimitiveProperty('timeRemaining', countdownCount);
    appStateUpdate.setPrimitiveProperty('numAnsweredCorrectly', correctCallerCount);
    appStateUpdate.setPrimitiveProperty('numAnsweredIncorrectly', incorrectCallerCount);
    appStateUpdate.setPrimitiveProperty('currentSceneIndexOnServer', parseInt(questionIndexInSourceSet));

    this.ingestUpdate(appStateUpdate);
  }

  /**
   * Handles UPDATE_STATE_ROUND_ENDPAUSE messages from MRTS, which supply the latest information for
   * the current scene's result.
   *
   * Delegates to processUpdateStateRoundEndPause() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  updateStateRoundEndPauseListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processUpdateStateRoundEndPause.bind(this), args, this.roomID);
  }

  /**
   * Processes UPDATE_STATE_ROUND_ENDPAUSE messages from MRTS.
   */
  processUpdateStateRoundEndPause (questionNum,
                                   totalNumQuestions,
                                   numCallers,  // Deprecated. Replaced by DATA_GLOBAL (s18)'s currentUsers.
                                   correctCallerCount,
                                   incorrectCallerCount,
                                   totalResponsesPerAnswer,
                                   questionIndexInSourceSet,
                                   countdownCount) {
    let appStateUpdate = new AppStateUpdate(this.appState, STATE_SCENE_RESULT);
    appStateUpdate.setCompositeProperty('votes', this.parseVotes(totalResponsesPerAnswer));
    appStateUpdate.setPrimitiveProperty('numAnsweredCorrectly', correctCallerCount);
    appStateUpdate.setPrimitiveProperty('numAnsweredIncorrectly', incorrectCallerCount);
    appStateUpdate.setPrimitiveProperty('timeRemaining', countdownCount);

    this.ingestUpdate(appStateUpdate);
  }


  /**
   * Handles UPDATE_STATE_MAIN_SCOREBOARD messages from MRTS, which supply the rankings and scores
   * for runs with quiz questions.
   *
   * Delegates to processUpdateStateMainScoreboard() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  updateStateMainScoreboardListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processUpdateStateMainScoreboard.bind(this), args, this.roomID);
  }

  /**
   * Processes UPDATE_STATE_MAIN_SCOREBOARD messages from MRTS.
   */
  processUpdateStateMainScoreboard (rankings) {
    let appStateUpdate = new AppStateUpdate(this.appState, STATE_MAIN_SCOREBOARD);
    appStateUpdate.setDirectProperty('overallRankings',this.parseRankings(rankings));

    this.ingestUpdate(appStateUpdate);
  }

  /**
   * Handles UPDATE_STATE_GAME_WINNER messages from MRTS, which supply the winner details for quiz runs.
   *
   * Delegates to processUpdateStateGameWinner() due to the need to strip the roomID argument when
   * the UPC broadcastType is "to_rooms".
   */
  updateStateGameWinnerListener (from, ...args) {
    MRTSConnection.adaptToBroadcastType(this.processUpdateStateGameWinner.bind(this), args, this.roomID);
  }

  /**
   * Processes UPDATE_STATE_GAME_WINNER messages from MRTS.
   */
  processUpdateStateGameWinner (winnerLabel) {
    let appStateUpdate = new AppStateUpdate(this.appState, STATE_WINNER);
    appStateUpdate.setDirectProperty('winner', {label: winnerLabel});

    this.ingestUpdate(appStateUpdate);
  }


  /**
   * Parses votes from MRTS. If votes have changed, assigns them to the app state.
   *
   * @param totalResponsesPerAnswer A string of votes from MRTS, in the format:
   *         votesForAnswer1|votesForAnswer2|votesForAnswer3|votesForAnswerN
   *
   *         E.g.,:
   *         "22|45|76"
   */
  parseVotes (totalResponsesPerAnswer) {
    if (totalResponsesPerAnswer === '') {
      return [];
    } else {
      return totalResponsesPerAnswer.split('|').map(item => parseInt(item));
    }
  }

  /**
   * Parses and returns rankings and scores from MRTS.
   *
   * @param rankings A string of rankings from MRTS, in the format:
   *         userID1|userLabel1|score1|userID2|userLabel2|score2|...
   *
   *         Note that userID is no longer used nor sent by MRTS, but continues to exist in
   *         the message as a legacy placeholder.
   *
   *         E.g.,:
   *         ",Viewer3916,848,,Viewer8375,346"
   */
  parseRankings (rankings) {
    let rankingsStrings = isNil(rankings) || rankings === '' ? [] : rankings.split(',');
    let rankingsArray = [];
    let user;

    for (let i = 0; i < rankingsStrings.length; i += 3) {
      user = {};
      user.id = rankingsStrings[i];
      user.label = rankingsStrings[i+1];
      user.score  = parseInt(rankingsStrings[i+2]);

      rankingsArray.push(user);
    }
    return rankingsArray;
  }

  /**
   * Parses and returns recent user responses from MRTS.
   *
   * @param responses A string of responses from MRTS, in the format:
   *         userLabel1|response1|userLabel2|response2|...
   *
   *         where responsen is the 1-based index of the user's answer to the current
   *         question.
   *
   *         E.g.,:
   *         "Viewer3916|2|Viewer8375|1"
   */
  parseResponses (responses = '') {
    let responsesStrings = responses === '' ? [] : responses.split('|');
    let responsesArray = [];
    let user;

    for (let i = 0; i < responsesStrings.length; i += 2) {
      user = {};
      user.label = responsesStrings[i];
      user.response = parseInt(responsesStrings[i+1]);

      responsesArray.push(user);
    }
    return responsesArray;
  }

  /**
   * Moves the application state to the specified state in response to a MRTS set-state message.
   *
   * @param state The name of the new state.
   */
  setAppState (state) {
    // If there's a buffered update waiting to be applied for this state...
    if (notNil(this.bufferedUpdate) && this.bufferedUpdate.targetState === state) {
      // ...apply the buffered update now
      this.bufferedUpdate.commit(true);
    }
    this.appState.runstate = state;
  }

  /**
   * Handles the receipt of an update from MRTS, and determines whether to apply that update immediately or
   * buffer it to be applied in the future (when the app state changes).
   *
   * @param update An AppStateUpdate object.
   * @param state A state name.
   */
  ingestUpdate (update) {
    if (this.appState.state !== update.targetState) {
      // This update was received in advance of the expected upcoming SET_STATE_* message, so buffer
      // it for now, and apply it when the SET_STATE_* message arrives (see setAppState()).
      this.bufferedUpdate = update;
    } else {
      // This update is intended for the current state, so apply it immediately.
      update.commit();
    }
  }
}

// ===============================================================================================
// STATIC FUNCTIONS: ORBITER UTILS
// ===============================================================================================
/**
 * Converts the supplied object's "key/value" properties into arguments formatted for use with
 * Orbiter's sendUPC(). For example, converts the following object...
 * {
 *     'size': 'large',
 *     'color': 'red'
 * }
 *
 * to the array:
 * ['size|large',
 *  'color|red']
 *
 * Usage example:
 *   let args = MRTSConnection.toUPCArgs({
 *     'size': 'large',
 *     'color': 'red'
 *   });
 *
 *   orbiter.getMessageManager().sendUPC(net.user1.orbiter.UPC.SEND_SERVERMODULE_MESSAGE,
 *     'MegaphoneServices',
 *     "SOME_COMMAND",
 *     ...args);
 *
 * @param obj
 * @returns {Array} An array suitable for use as the arguments provided to Orbiter's sendUPC() method.
 */
MRTSConnection.toUPCArgs = (obj) => {
  let args = [];
  for (let key in obj) {
    args.push(`${key}|${obj[key]}`);
  }
  return args;
};

/**
 * Invokes the specified message-listener function with the appropriate message-listener arguments.
 * For messages with broadcastType "to_rooms", adaptToBroadcastType() the toRoom argument
 * before invoking the message-listener function.
 *
 * For details, see "u7" under: http://unionplatform.com/specs/upc/
 *
 * The adaptToBroadcastType() method is required because MRTS sends Megaphone module messages with
 * two different broadcastType settings. During client initialization, MRTS sends some module
 * messages "TO CLIENTS" (broadcastType 2). Then, once the client is in the room, MRTS sends
 * module messages "TO ROOMS" (broadcastType 1). The adaptToBroadcastType() allows a single
 * listener function to handle both broadcast types with the same parameter list.
 */
MRTSConnection.adaptToBroadcastType = function (func, args, roomID) {
  // Detect whether the UPC message broadcast type was "TO CLIENTS" or "TO ROOMS", as follows:
  // If the first argument is an Orbiter Room instance (as would be the case for "TO ROOMS" messages
  // in the Orbiter library), OR the first argument is the specified application roomID (as would be
  // the case for "TO ROOMS" messages in the OrbiterMicro library), then the broadcastType is
  // "to_rooms". Otherwise, the broadcast type is "to_clients".
  let upcMessageType = (notNil(get(net, 'user1.orbiter.Room')) && args[0] instanceof net.user1.orbiter.Room) || (args[0] === roomID) ? 'to_rooms' : 'to_clients';
  if (upcMessageType === 'to_rooms') {
    // Discard the roomID argument
    args.shift();
  }
  func(...args);
};

// ===============================================================================================
// STATIC VARIABLES
// ===============================================================================================
MRTSConnection.AUTO_RECONNECT_FREQUENCY = 5000;
MRTSConnection.CONNECTION_TIMEOUT = 7000;
MRTSConnection.SCREEN_TIMEOUT = 8000;
MRTSConnection.HEARTBEAT_FREQUENCY = 5000;
MRTSConnection.HUMAN_READABLE_CONNECTION_STATES = new Map();
MRTSConnection.HUMAN_READABLE_CONNECTION_STATES.set(0, 'NOT_CONNECTED');
MRTSConnection.HUMAN_READABLE_CONNECTION_STATES.set(1, 'READY');
MRTSConnection.HUMAN_READABLE_CONNECTION_STATES.set(2, 'CONNECTION_IN_PROGRESS');
MRTSConnection.HUMAN_READABLE_CONNECTION_STATES.set(3, 'DISCONNECTION_IN_PROGRESS');
MRTSConnection.HUMAN_READABLE_CONNECTION_STATES.set(4, 'LOGGED_IN');
